import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Settings, Key, Phone, Video, Users, Shield, Save, CreditCard, Mail,
  Eye, EyeOff, CheckCircle, AlertTriangle, Loader2, ExternalLink, Home, RefreshCw, Radio
} from 'lucide-react';
import { stripeApi, integrationSettingsApi } from '../../lib/api';
import { useSearchParams } from 'react-router-dom';

export default function AdminSettingsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isPlatformAdmin = user?.role === 'super_admin';
  const isPracticeAdmin = user?.role === 'practice_admin';
  const canManageHha = isPlatformAdmin || isPracticeAdmin;
  const requestedTab = searchParams.get('tab');
  const defaultTab = !isPlatformAdmin ? 'billing' : (requestedTab || 'integrations');
  const sessionId = searchParams.get('session_id');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  
  const [integrations, setIntegrations] = useState({
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_api_key_sid: '',
    twilio_api_key_secret: '',
    twilio_phone_number: '',
    // HHA Exchange (home-care EVV / clinical integration)
    hha_api_url: '',
    hha_api_key: '',
    hha_api_secret: '',
    hha_agency_id: '',
  });

  const [users, setUsers] = useState([]);
  const [impersonating, setImpersonating] = useState(false);
  const [hhaStatus, setHhaStatus] = useState(null);
  const [hhaSyncing, setHhaSyncing] = useState(false);

  useEffect(() => {
    loadSettings();
    loadUsers();
    loadHhaStatus();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/settings/integrations');
      setIntegrations(prev => ({
        ...prev,
        twilio_account_sid: response.data.twilio_account_sid || '',
        twilio_phone_number: response.data.twilio_phone_number || '',
        hha_api_url: response.data.hha_api_url || '',
        hha_agency_id: response.data.hha_agency_id || '',
        // api_key/secret are write-only (never echoed from server); leave blank unless user sets them
      }));
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHhaStatus = async () => {
    try {
      const res = await api.get('/admin/hha/sync-status');
      setHhaStatus(res.data);
    } catch (e) {
      // silent — only super_admins can view
    }
  };

  const handleHhaSyncNow = async () => {
    setHhaSyncing(true);
    try {
      const res = await api.post('/admin/hha/sync');
      const mode = res.data?.mode || 'unknown';
      const counts = res.data?.counts || {};
      toast.success(`HHA sync complete (${mode}) — ${counts.hha_patients || 0} patients, ${counts.hha_visits || 0} visits`);
      await loadHhaStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'HHA sync failed');
    } finally {
      setHhaSyncing(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleSaveIntegrations = async () => {
    setSaving(true);
    try {
      await api.post('/admin/settings/integrations', integrations);
      toast.success('Integration settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      setImpersonating(true);
      const response = await api.post(`/admin/impersonate/${userId}`);
      toast.success(`Now impersonating ${response.data.impersonating}`);
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error('Failed to impersonate user');
      setImpersonating(false);
    }
  };

  const toggleSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-[#991B1B]',
      practice_admin: 'bg-[#0A2342]',
      physician: 'bg-[#0E7490]',
      nurse: 'bg-[#1A56DB]',
      billing_specialist: 'bg-[#92400E]',
      patient: 'bg-[#166534]'
    };
    return `${colors[role] || 'bg-[#64748B]'} text-white`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {isPlatformAdmin ? 'Platform Settings' : (isPracticeAdmin ? 'Organization Settings' : 'Billing & Subscription')}
        </h1>
        <p className="text-[#64748B] mt-1">
          {isPlatformAdmin ? 'Manage integrations, users, and platform configuration' : (isPracticeAdmin ? 'Manage your organization\'s integrations and billing' : 'Manage your subscription plan and billing history')}
        </p>
      </div>

      <Tabs defaultValue={isPracticeAdmin && !requestedTab ? 'integrations' : defaultTab}>
        <TabsList className={`grid w-full ${isPlatformAdmin ? 'grid-cols-5 max-w-2xl' : (isPracticeAdmin ? 'grid-cols-2 max-w-md' : 'grid-cols-1 max-w-xs')}`}>
          {canManageHha && <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>}
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          {isPlatformAdmin && <TabsTrigger value="secrets" data-testid="tab-secrets">API Keys</TabsTrigger>}
          {isPlatformAdmin && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
          {isPlatformAdmin && <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>}
        </TabsList>

        {/* Billing Tab (available to both super_admin and practice_admin) */}
        <TabsContent value="billing" className="mt-6">
          <BillingPanel />
        </TabsContent>

        {/* API Keys Tab — PLATFORM ADMIN ONLY */}
        {user?.role === 'super_admin' && (
          <TabsContent value="secrets" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <IntegrationSecretCard provider="stripe" />
              <IntegrationSecretCard provider="resend" />
            </div>
          </TabsContent>
        )}

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="mt-6">
          {!canManageHha ? (
            <Card className="careiq-card"><CardContent className="py-12 text-center text-[#64748B]"><Shield className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" /><p>Platform integrations are managed by your administrator.</p></CardContent></Card>
          ) : (
          <div className="space-y-4">
            {/* Scope banner */}
            <div className={`p-3 rounded-lg text-xs flex items-start gap-2 ${isPlatformAdmin ? 'bg-[#F0F9FF] border border-[#BAE6FD] text-[#075985]' : 'bg-[#F0FDFA] border border-[#A7F3D0] text-[#065F46]'}`} data-testid="integrations-scope-banner">
              <Shield className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                {isPlatformAdmin ? (
                  <><strong>Platform-wide scope.</strong> Credentials you save here apply to every organization that hasn't configured their own. Practice admins can also configure HHA Exchange per-org from their own Settings page.</>
                ) : (
                  <><strong>Organization scope.</strong> HHA Exchange credentials you save here apply only to your organization. Twilio SMS/Video is platform-managed.</>
                )}
              </div>
            </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Twilio Settings — PLATFORM ADMIN ONLY */}
            {isPlatformAdmin && (
            <Card className="careiq-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#0E7490]" />
                  Twilio SMS & Video
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-[#0E7490]/10 rounded-lg text-sm text-[#0E7490]">
                  Configure Twilio for SMS appointment reminders and telehealth video calls.
                </div>

                <div>
                  <Label className="careiq-label">Account SID</Label>
                  <Input
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={integrations.twilio_account_sid}
                    onChange={(e) => setIntegrations({ ...integrations, twilio_account_sid: e.target.value })}
                    className="careiq-input"
                    data-testid="twilio-account-sid"
                  />
                </div>

                <div>
                  <Label className="careiq-label">Auth Token</Label>
                  <div className="relative">
                    <Input
                      type={showSecrets.auth_token ? 'text' : 'password'}
                      placeholder="Enter Auth Token"
                      value={integrations.twilio_auth_token}
                      onChange={(e) => setIntegrations({ ...integrations, twilio_auth_token: e.target.value })}
                      className="careiq-input pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => toggleSecret('auth_token')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                    >
                      {showSecrets.auth_token ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="careiq-label">API Key SID (for Video)</Label>
                  <Input
                    placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={integrations.twilio_api_key_sid}
                    onChange={(e) => setIntegrations({ ...integrations, twilio_api_key_sid: e.target.value })}
                    className="careiq-input"
                  />
                </div>

                <div>
                  <Label className="careiq-label">API Key Secret</Label>
                  <div className="relative">
                    <Input
                      type={showSecrets.api_secret ? 'text' : 'password'}
                      placeholder="Enter API Key Secret"
                      value={integrations.twilio_api_key_secret}
                      onChange={(e) => setIntegrations({ ...integrations, twilio_api_key_secret: e.target.value })}
                      className="careiq-input pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => toggleSecret('api_secret')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                    >
                      {showSecrets.api_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="careiq-label">Phone Number</Label>
                  <Input
                    placeholder="+1234567890"
                    value={integrations.twilio_phone_number}
                    onChange={(e) => setIntegrations({ ...integrations, twilio_phone_number: e.target.value })}
                    className="careiq-input"
                  />
                </div>

                <Button 
                  className="careiq-btn-primary w-full flex items-center justify-center gap-2"
                  onClick={handleSaveIntegrations}
                  disabled={saving}
                  data-testid="save-integrations-btn"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Twilio Settings
                </Button>
              </CardContent>
            </Card>
            )}

            {/* HHA Exchange Integration (Home Care EVV) — AVAILABLE TO BOTH super_admin AND practice_admin */}
            <Card className="careiq-card" data-testid="hha-exchange-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                  <Home className="w-5 h-5 text-[#0E7490]" />
                  HHA Exchange
                </CardTitle>
                <p className="text-xs text-[#64748B]">Connect your HHA Exchange account to sync patient rosters, visits, EVV, and authorizations.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="careiq-label">API Base URL</Label>
                  <Input
                    placeholder="https://api.hhaexchange.com/v1"
                    value={integrations.hha_api_url}
                    onChange={(e) => setIntegrations({ ...integrations, hha_api_url: e.target.value })}
                    className="careiq-input"
                    data-testid="hha-api-url-input"
                  />
                </div>
                <div>
                  <Label className="careiq-label">Agency ID</Label>
                  <Input
                    placeholder="your-agency-id"
                    value={integrations.hha_agency_id}
                    onChange={(e) => setIntegrations({ ...integrations, hha_agency_id: e.target.value })}
                    className="careiq-input"
                    data-testid="hha-agency-id-input"
                  />
                </div>
                <div>
                  <Label className="careiq-label">API Key</Label>
                  <div className="relative">
                    <Input
                      type={showSecrets.hha_key ? 'text' : 'password'}
                      placeholder="hha_••••••••••••"
                      value={integrations.hha_api_key}
                      onChange={(e) => setIntegrations({ ...integrations, hha_api_key: e.target.value })}
                      className="careiq-input pr-10"
                      data-testid="hha-api-key-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecrets({ ...showSecrets, hha_key: !showSecrets.hha_key })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                    >
                      {showSecrets.hha_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="careiq-label">API Secret</Label>
                  <div className="relative">
                    <Input
                      type={showSecrets.hha_secret ? 'text' : 'password'}
                      placeholder="hha_secret_••••••••••••"
                      value={integrations.hha_api_secret}
                      onChange={(e) => setIntegrations({ ...integrations, hha_api_secret: e.target.value })}
                      className="careiq-input pr-10"
                      data-testid="hha-api-secret-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecrets({ ...showSecrets, hha_secret: !showSecrets.hha_secret })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]"
                    >
                      {showSecrets.hha_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="p-3 rounded border border-[#E2E8F0] bg-[#F0FDFA] text-[11px] text-[#0A2342]">
                  <strong>Where do I find these?</strong> Log in to your HHA Exchange admin portal → Settings → API Access. You'll need to request API credentials from your HHA Exchange account representative if they're not already enabled.
                </div>
                <Button
                  className="careiq-btn-primary w-full flex items-center justify-center gap-2"
                  onClick={handleSaveIntegrations}
                  disabled={saving}
                  data-testid="save-hha-btn"
                >
                  {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                  Save HHA Exchange Credentials
                </Button>

                {/* Sync status + manual trigger */}
                <div className="mt-3 p-3 rounded border border-[#E2E8F0] bg-white" data-testid="hha-sync-control">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-[#0A2342] flex items-center gap-2">
                        <Radio className="w-3.5 h-3.5 text-[#0E7490]" />
                        Background Sync
                      </div>
                      {hhaStatus?.last_sync ? (
                        <div className="text-[11px] text-[#64748B] mt-1">
                          Last run: <span className="font-medium text-[#0A2342]">{new Date(hhaStatus.last_sync.finished_at).toLocaleString()}</span>
                          {' '}({hhaStatus.last_sync.mode})
                          {hhaStatus.last_sync.counts && (
                            <> — synced {hhaStatus.last_sync.counts.hha_patients || 0} patients, {hhaStatus.last_sync.counts.hha_visits || 0} visits, {hhaStatus.last_sync.counts.hha_evv_records || 0} EVV events</>
                          )}
                        </div>
                      ) : (
                        <div className="text-[11px] text-[#64748B] mt-1">Never synced yet. Runs automatically every 24h once configured.</div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 shrink-0"
                      onClick={handleHhaSyncNow}
                      disabled={hhaSyncing}
                      data-testid="hha-sync-now-btn-admin"
                    >
                      <RefreshCw className={`w-4 h-4 ${hhaSyncing ? 'animate-spin' : ''}`} />
                      {hhaSyncing ? 'Syncing…' : 'Sync Now'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Integration Status */}
            <Card className="careiq-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#0A2342]">Integration Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isPlatformAdmin && (
                <div className="flex items-center justify-between p-4 rounded-lg bg-[#F8FAFC]">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-[#0E7490]" />
                    <div>
                      <div className="font-medium text-[#0A2342]">Twilio SMS</div>
                      <div className="text-sm text-[#64748B]">Appointment reminders</div>
                    </div>
                  </div>
                  {integrations.twilio_account_sid ? (
                    <CheckCircle className="w-5 h-5 text-[#166534]" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-[#92400E]" />
                  )}
                </div>
                )}

                {isPlatformAdmin && (
                <div className="flex items-center justify-between p-4 rounded-lg bg-[#F8FAFC]">
                  <div className="flex items-center gap-3">
                    <Video className="w-5 h-5 text-[#1A56DB]" />
                    <div>
                      <div className="font-medium text-[#0A2342]">Twilio Video</div>
                      <div className="text-sm text-[#64748B]">Telehealth sessions</div>
                    </div>
                  </div>
                  {integrations.twilio_api_key_sid ? (
                    <CheckCircle className="w-5 h-5 text-[#166534]" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-[#92400E]" />
                  )}
                </div>
                )}

                <div className="flex items-center justify-between p-4 rounded-lg bg-[#F8FAFC]">
                  <div className="flex items-center gap-3">
                    <Home className="w-5 h-5 text-[#0E7490]" />
                    <div>
                      <div className="font-medium text-[#0A2342]">HHA Exchange</div>
                      <div className="text-sm text-[#64748B]">{isPracticeAdmin ? 'Per-organization sync' : 'Platform-wide roster sync'}</div>
                    </div>
                  </div>
                  {hhaStatus?.configured || integrations.hha_api_url ? (
                    <CheckCircle className="w-5 h-5 text-[#166534]" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-[#92400E]" />
                  )}
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-[#F8FAFC]">
                  <div className="flex items-center gap-3">
                    <Key className="w-5 h-5 text-[#166534]" />
                    <div>
                      <div className="font-medium text-[#0A2342]">AI Clinical Assistant</div>
                      <div className="text-sm text-[#64748B]">Claude Sonnet integration</div>
                    </div>
                  </div>
                  <CheckCircle className="w-5 h-5 text-[#166534]" />
                </div>
              </CardContent>
            </Card>
          </div>
          </div>
          )}
        </TabsContent>

        {/* Users Tab — PLATFORM ADMIN ONLY */}
        {user?.role === 'super_admin' && (
        <TabsContent value="users" className="mt-6">
          <Card className="careiq-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                <Users className="w-5 h-5 text-[#0E7490]" />
                User Management
              </CardTitle>
              <div className="text-sm text-[#64748B]">{users.length} users</div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E2E8F0]">
                      <th className="table-header text-left">User</th>
                      <th className="table-header text-left">Email</th>
                      <th className="table-header text-left">Role</th>
                      <th className="table-header text-left">MFA</th>
                      <th className="table-header text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="table-row">
                        <td className="table-cell font-medium">{u.name}</td>
                        <td className="table-cell text-[#64748B]">{u.email}</td>
                        <td className="table-cell">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(u.role)}`}>
                            {u.role.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="table-cell">
                          {u.mfa_enabled ? (
                            <CheckCircle className="w-4 h-4 text-[#166534]" />
                          ) : (
                            <span className="text-[#64748B]">-</span>
                          )}
                        </td>
                        <td className="table-cell">
                          {u.id !== user.id && u.role !== 'super_admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleImpersonate(u.id)}
                              disabled={impersonating}
                              className="text-[#0E7490]"
                              data-testid={`impersonate-${u.id}`}
                            >
                              Impersonate
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Security Tab — PLATFORM ADMIN ONLY */}
        {user?.role === 'super_admin' && (
        <TabsContent value="security" className="mt-6">
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#0E7490]" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-[#166534]/10 border border-[#166534] rounded-lg">
                <div className="flex items-center gap-2 text-[#166534] font-medium mb-2">
                  <CheckCircle className="w-5 h-5" />
                  HIPAA Compliant Configuration
                </div>
                <ul className="text-sm text-[#334155] space-y-1 ml-7">
                  <li>• JWT authentication with httpOnly cookies</li>
                  <li>• Brute force protection (5 attempts, 15-min lockout)</li>
                  <li>• MFA support via TOTP</li>
                  <li>• Audit logging enabled</li>
                  <li>• No PHI in application logs</li>
                </ul>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-[#F8FAFC]">
                  <div className="text-sm text-[#64748B]">Session Timeout</div>
                  <div className="text-xl font-semibold text-[#0A2342]">15 minutes</div>
                </div>
                <div className="p-4 rounded-lg bg-[#F8FAFC]">
                  <div className="text-sm text-[#64748B]">Password Policy</div>
                  <div className="text-xl font-semibold text-[#0A2342]">12+ characters</div>
                </div>
                <div className="p-4 rounded-lg bg-[#F8FAFC]">
                  <div className="text-sm text-[#64748B]">Failed Login Lockout</div>
                  <div className="text-xl font-semibold text-[#0A2342]">5 attempts</div>
                </div>
                <div className="p-4 rounded-lg bg-[#F8FAFC]">
                  <div className="text-sm text-[#64748B]">Refresh Token Validity</div>
                  <div className="text-xl font-semibold text-[#0A2342]">7 days</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ============== BillingPanel ==============
function BillingPanel() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const canceled = searchParams.get('canceled');
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [txns, setTxns] = useState([]);
  const [cycle, setCycle] = useState('monthly');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState(null); // { valid, code, discount_type, discount_value, reason }
  const [validatingPromo, setValidatingPromo] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [p, s] = await Promise.all([stripeApi.plans(), stripeApi.subscription()]);
        setPlans(p.data.plans || []);
        setSubscription(s.data.subscription);
        setTxns(s.data.transactions || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // Poll payment status on return from Stripe
  useEffect(() => {
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const r = await stripeApi.status(sessionId);
        if (r.data.payment_status === 'paid') {
          clearInterval(interval);
          setPolling(false);
          toast.success('Subscription activated!');
          const s = await stripeApi.subscription();
          setSubscription(s.data.subscription);
          setTxns(s.data.transactions || []);
        } else if (r.data.status === 'expired' || attempts >= 10) {
          clearInterval(interval);
          setPolling(false);
          toast.error('Payment did not complete in time');
        }
      } catch { /* keep trying */ }
      if (attempts >= 10) { clearInterval(interval); setPolling(false); }
    }, 2000);
    return () => clearInterval(interval);
  }, [sessionId]);

  useEffect(() => { if (canceled) toast.info('Checkout canceled'); }, [canceled]);

  const handleSubscribe = async (planId) => {
    setBusy(true);
    try {
      // Free trial plans skip Stripe — activate directly via local subscription
      const plan = plans.find(p => p.plan_id === planId);
      if (plan && plan.monthly_price === 0 && plan.yearly_price === 0) {
        const { default: axios } = await import('axios');
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('savenes_token');
        await axios.post(`${API_URL}/api/stripe/start-trial`,
          { plan_id: planId },
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success('7-day trial started!');
        const s = await stripeApi.subscription();
        setSubscription(s.data.subscription);
        setBusy(false);
        return;
      }
      const origin = window.location.origin;
      const trimmedPromo = (promoCode || '').trim();
      const payload = { plan_id: planId, billing_cycle: cycle, origin_url: origin };
      if (trimmedPromo) payload.promo_code = trimmedPromo;
      const r = await stripeApi.createCheckout(payload);
      window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Checkout failed'); setBusy(false); }
  };

  const handleValidatePromo = async () => {
    const code = (promoCode || '').trim();
    if (!code) { setPromoStatus(null); return; }
    setValidatingPromo(true);
    try {
      const { default: axios } = await import('axios');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const r = await axios.post(`${API_URL}/api/public/validate-promo`, { code });
      setPromoStatus(r.data);
      if (r.data.valid) toast.success(`Promo applied: ${r.data.discount_type === 'percent' ? r.data.discount_value + '%' : '$' + r.data.discount_value} off`);
      else toast.error(r.data.reason || 'Invalid promo code');
    } catch { toast.error('Could not validate promo code'); setPromoStatus(null); }
    finally { setValidatingPromo(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription at the end of the current period?')) return;
    try {
      await stripeApi.cancel();
      toast.success('Subscription scheduled for cancellation');
      const s = await stripeApi.subscription();
      setSubscription(s.data.subscription);
    } catch { toast.error('Cancel failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#0E7490] animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="billing-panel">
      {polling && <div className="bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E] p-3 rounded-lg text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Verifying payment...</div>}
      {subscription && subscription.status === 'active' && (
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg text-[#0A2342] flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#166534]" />Current Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-[#64748B]">Plan</div>
                <div className="text-xl font-bold text-[#0A2342] capitalize">{subscription.plan_id} ({subscription.billing_cycle})</div>
                <div className="text-xs text-[#94A3B8] mt-1">Activated {subscription.activated_at ? new Date(subscription.activated_at).toLocaleDateString() : ''}</div>
              </div>
              <Button variant="outline" className="text-[#991B1B]" onClick={handleCancel} data-testid="cancel-subscription-btn">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-[#0A2342]">Choose a Plan</h2>
          <div className="flex bg-[#F1F5F9] rounded-lg p-0.5">
            <button onClick={() => setCycle('monthly')} className={`px-3 py-1 rounded text-xs font-medium ${cycle === 'monthly' ? 'bg-white shadow-sm text-[#0E7490]' : 'text-[#64748B]'}`} data-testid="cycle-monthly">Monthly</button>
            <button onClick={() => setCycle('yearly')} className={`px-3 py-1 rounded text-xs font-medium ${cycle === 'yearly' ? 'bg-white shadow-sm text-[#0E7490]' : 'text-[#64748B]'}`} data-testid="cycle-yearly">Yearly (2mo free)</button>
          </div>
        </div>

        {/* Promo code input — validated against backend before checkout */}
        <div className="mb-4 p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg" data-testid="promo-code-section">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs font-medium text-[#64748B] uppercase tracking-wide">Promo code</label>
            <input
              type="text"
              value={promoCode}
              onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus(null); }}
              placeholder="Enter code"
              className="careiq-input h-8 text-sm uppercase max-w-[180px] tracking-wider"
              data-testid="promo-code-input"
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={handleValidatePromo}
              disabled={!promoCode.trim() || validatingPromo}
              data-testid="apply-promo-btn"
            >
              {validatingPromo ? 'Checking…' : 'Apply'}
            </Button>
            {promoStatus?.valid && (
              <span className="text-xs font-medium text-[#166534] flex items-center gap-1" data-testid="promo-valid">
                <CheckCircle className="w-3.5 h-3.5" />
                {promoStatus.discount_type === 'percent'
                  ? `${promoStatus.discount_value}% off applied`
                  : `$${promoStatus.discount_value} off applied`}
              </span>
            )}
            {promoStatus && !promoStatus.valid && (
              <span className="text-xs text-[#991B1B]" data-testid="promo-invalid">
                {promoStatus.reason || 'Invalid code'}
              </span>
            )}
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-1">Discount is applied at checkout. Some codes only apply to specific plans.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map(p => {
            const price = cycle === 'yearly' ? p.yearly_price : p.monthly_price;
            const isCurrent = subscription?.plan_id === p.plan_id && subscription?.status === 'active';
            const isTrial = (p.monthly_price === 0 && p.yearly_price === 0) || p.plan_id === 'forms_trial';
            return (
              <Card key={p.plan_id} className={`careiq-card ${isCurrent ? 'border-[#0E7490] border-2' : ''} ${isTrial ? 'border-l-4 border-l-emerald-500' : ''}`} data-testid={`plan-${p.plan_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-semibold text-[#0A2342]">{p.name}</div>
                    {isTrial && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">7-DAY TRIAL</span>}
                  </div>
                  {p.description && <p className="text-xs text-[#64748B] mt-1">{p.description}</p>}
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-[#0A2342]">${price}</span>
                    <span className="text-sm text-[#64748B]">/{cycle === 'yearly' ? 'yr' : 'mo'}</span>
                  </div>
                  <ul className="mt-4 space-y-1 text-xs text-[#64748B]">
                    {p.features.map((f, i) => <li key={i} className="flex items-start gap-1"><CheckCircle className="w-3 h-3 text-[#166534] mt-0.5 shrink-0" />{f}</li>)}
                  </ul>
                  <Button
                    className={`w-full mt-4 ${isCurrent ? 'bg-[#94A3B8] cursor-not-allowed' : isTrial ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'careiq-btn-primary'}`}
                    disabled={isCurrent || busy}
                    onClick={() => handleSubscribe(p.plan_id)}
                    data-testid={`subscribe-${p.plan_id}`}
                  >
                    {isCurrent ? 'Current Plan' : isTrial ? 'Start 7-day trial' : 'Subscribe'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {txns.length > 0 && (
        <Card className="careiq-card">
          <CardHeader><CardTitle className="text-lg text-[#0A2342]">Invoice History</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-[#64748B] border-b"><th className="text-left py-2">Date</th><th className="text-left">Plan</th><th className="text-left">Amount</th><th className="text-left">Status</th></tr></thead>
              <tbody>
                {txns.map((t, i) => (
                  <tr key={i} className="border-b border-[#F1F5F9]" data-testid={`txn-${i}`}>
                    <td className="py-2 text-xs">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</td>
                    <td className="text-xs capitalize">{t.plan_id} ({t.billing_cycle})</td>
                    <td className="text-xs">${t.amount}</td>
                    <td className="text-xs">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${t.payment_status === 'paid' ? 'bg-[#166534]/10 text-[#166534]' : 'bg-[#FEF3C7] text-[#92400E]'}`}>{t.payment_status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============== IntegrationSecretCard ==============
function IntegrationSecretCard({ provider }) {
  const isStripe = provider === 'stripe';
  const [info, setInfo] = useState({ configured: false, masked_api_key: '' });
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const r = await integrationSettingsApi.get(provider);
      setInfo(r.data);
      setFromEmail(r.data.from_email || '');
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!apiKey && !webhookSecret && !fromEmail) { toast.error('Nothing to save'); return; }
    setSaving(true);
    try {
      const payload = {};
      if (apiKey) payload.api_key = apiKey;
      if (webhookSecret) payload.webhook_secret = webhookSecret;
      if (!isStripe && fromEmail) payload.from_email = fromEmail;
      await integrationSettingsApi.save(provider, payload);
      toast.success(`${provider} settings saved`);
      setApiKey(''); setWebhookSecret('');
      load();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await integrationSettingsApi.test(provider);
      if (r.data.ok) toast.success(r.data.message || 'Connection OK');
      else toast.error(r.data.error || 'Test failed');
    } catch { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  return (
    <Card className="careiq-card" data-testid={`integration-card-${provider}`}>
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
          {isStripe ? <CreditCard className="w-5 h-5 text-[#635BFF]" /> : <Mail className="w-5 h-5 text-[#000000]" />}
          {isStripe ? 'Stripe (Recurring Payments)' : 'Resend (Transactional Email)'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-xs">
          {info.configured ? (
            <><CheckCircle className="w-4 h-4 text-[#166534]" /><span className="text-[#166534]">Configured</span>
              {info.source === 'env' && <span className="text-[#94A3B8]">(from env)</span>}
            </>
          ) : (
            <><AlertTriangle className="w-4 h-4 text-[#92400E]" /><span className="text-[#92400E]">Not configured</span></>
          )}
        </div>
        {info.masked_api_key && <div className="text-xs text-[#64748B]">Key: <code className="bg-[#F1F5F9] px-1.5 py-0.5 rounded">{info.masked_api_key}</code></div>}
        <div>
          <Label className="text-xs">{isStripe ? 'Stripe Secret Key (sk_live_… or sk_test_…)' : 'Resend API Key (re_…)'}</Label>
          <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder={isStripe ? 'sk_live_...' : 're_...'} data-testid={`api-key-input-${provider}`} />
        </div>
        {isStripe && (
          <div>
            <Label className="text-xs">Stripe Webhook Signing Secret (whsec_…)</Label>
            <Input type="password" value={webhookSecret} onChange={e => setWebhookSecret(e.target.value)} placeholder="whsec_..." data-testid="webhook-secret-input-stripe" />
            <p className="text-[10px] text-[#94A3B8] mt-1">Webhook URL: <code className="bg-[#F1F5F9] px-1 rounded">{window.location.origin}/api/webhook/stripe</code></p>
          </div>
        )}
        {!isStripe && (
          <div>
            <Label className="text-xs">From Email (verified domain)</Label>
            <Input type="email" value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="noreply@your-domain.com" data-testid="from-email-input-resend" />
          </div>
        )}
        <div className="flex gap-2">
          <Button className="careiq-btn-primary" onClick={save} disabled={saving} data-testid={`save-${provider}`}>{saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save</Button>
          <Button variant="outline" onClick={test} disabled={testing} data-testid={`test-${provider}`}>{testing ? 'Testing…' : 'Test'}</Button>
          <a href={isStripe ? 'https://dashboard.stripe.com/apikeys' : 'https://resend.com/api-keys'} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0E7490] hover:underline flex items-center gap-1 ml-auto self-center">Get key <ExternalLink className="w-3 h-3" /></a>
        </div>
      </CardContent>
    </Card>
  );
}
